import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import XPHeader from "@/components/XPHeader";
import DecodeMiniCard from "@/components/DecodeMiniCard";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Progress } from "@/components/ui/progress";
import { Sparkles, BookOpen, Target, Gamepad2, Archive, ArrowRight, Loader2, Flame } from "lucide-react";
import { toast } from "sonner";

export default function Today() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [data, setData] = useState(null);
  const [quick, setQuick] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => { try { const r = await api.get("/today"); setData(r.data); } catch (e) { /* ignore */ } };
  useEffect(() => { load(); }, []);

  const decodeDaily = async () => {
    setBusy(true);
    try {
      const r = await api.post("/decode/mini", { text: data.daily_word.word, decode_type: "Status Word" });
      if (r.data.safety) { toast(r.data.safety.message); return; }
      setQuick(r.data); await Promise.all([load(), refresh()]);
    } catch (e) { toast.error(e?.response?.data?.detail || "Decode failed"); } finally { setBusy(false); }
  };

  const doSuggested = () => {
    const s = data?.suggested_next;
    if (!s) return;
    if (s.action === "decode") decodeDaily();
    else if (s.action === "lesson") navigate("/doctrine");
    else if (s.action === "mission") navigate("/decode");
    else if (s.action === "onboarding") navigate("/onboarding");
    else navigate("/decode");
  };

  if (!data) return <div className="space-y-4"><Skeleton className="h-28 w-full bg-white/[0.04]" /><Skeleton className="h-48 w-full bg-white/[0.04]" /></div>;

  const arch = data.archive_progress;

  return (
    <div className="space-y-5">
      <div className="flex items-end justify-between flex-wrap gap-2">
        <div><h1 className="text-2xl font-display font-semibold">Today</h1>
          <p className="text-[var(--bone-muted)] text-sm">Hi {data.user?.username} — your daily Litrametry loop.</p></div>
        <div className="flex items-center gap-1.5 text-sm" data-testid="today-streak"><Flame className="w-4 h-4 gold-text" />{data.streak || 0} day streak</div>
      </div>

      <XPHeader progress={data.progress} streak={data.streak} />

      {/* Suggested next action */}
      <button onClick={doSuggested} className="w-full text-left litra-card litra-card-hover p-5 flex items-center justify-between" style={{ borderColor: "rgba(20,90,67,0.45)" }} data-testid="today-suggested-action">
        <div><div className="mono-chip green-text">SUGGESTED NEXT</div><div className="mt-1 font-display font-semibold">{data.suggested_next?.label}</div></div>
        <ArrowRight className="w-5 h-5 green-text" />
      </button>

      <div className="grid lg:grid-cols-2 gap-4">
        {/* Daily word */}
        <div className="litra-card p-5" data-testid="today-daily-word">
          <div className="flex items-center justify-between"><div className="text-sm font-display font-semibold flex items-center gap-2"><Sparkles className="w-4 h-4 gold-text" />Daily Word</div><Badge className="gold-edge gold-text bg-transparent">+{data.daily_word?.reward_xp} XP</Badge></div>
          <div className="mt-2 text-2xl font-display font-semibold">{data.daily_word?.word}</div>
          <p className="text-sm text-[var(--bone-muted)] mt-1">{data.daily_word?.mini?.overview}</p>
          <Button onClick={decodeDaily} disabled={busy} className="mt-3 bg-[var(--primary)] hover:opacity-90" data-testid="today-decode-daily-button">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Decode {data.daily_word?.word}</>}</Button>
        </div>

        {/* Daily lesson */}
        <div className="litra-card p-5" data-testid="today-daily-lesson">
          <div className="text-sm font-display font-semibold flex items-center gap-2"><BookOpen className="w-4 h-4 green-text" />Daily Lesson</div>
          {data.daily_lesson ? (<>
            <div className="mt-2 font-display">{data.daily_lesson.title}</div>
            <p className="text-sm text-[var(--bone-muted)] mt-1">Year {data.daily_lesson.year} · +{data.daily_lesson.xp_reward} XP</p>
            <Button variant="outline" className="mt-3 glass" onClick={() => navigate("/doctrine")} data-testid="today-lesson-button">Open lesson</Button>
          </>) : <p className="text-sm text-[var(--bone-faint)] mt-2">All lessons complete — master status!</p>}
        </div>

        {/* Daily mission */}
        <div className="litra-card p-5" data-testid="today-daily-mission">
          <div className="text-sm font-display font-semibold flex items-center gap-2"><Target className="w-4 h-4 green-text" />Daily Mission</div>
          {data.daily_mission ? (<>
            <div className="mt-2 font-display">{data.daily_mission.title}</div>
            <p className="text-sm text-[var(--bone-muted)] mt-1">{data.daily_mission.description} · +{data.daily_mission.reward_xp} XP</p>
            <Button variant="outline" className="mt-3 glass" onClick={() => navigate("/decode")} data-testid="today-mission-button">Start mission</Button>
          </>) : <p className="text-sm text-[var(--bone-faint)] mt-2">All missions complete!</p>}
        </div>

        {/* Life + archive progress */}
        <div className="litra-card p-5" data-testid="today-progress">
          <div className="text-sm font-display font-semibold flex items-center gap-2"><Gamepad2 className="w-4 h-4 green-text" />Your Progress</div>
          <div className="mt-3 space-y-3">
            <div className="flex items-center justify-between text-sm">
              <span className="text-[var(--bone-muted)]">Path Mode</span>
              {data.life_progress ? <span className="capitalize">{data.life_progress.path?.replace("_", " ")} · Age {data.life_progress.age}</span> : <button className="green-text" onClick={() => navigate("/path")}>Start a run</button>}
            </div>
            <div>
              <div className="flex items-center justify-between text-sm mb-1"><span className="text-[var(--bone-muted)] flex items-center gap-1.5"><Archive className="w-3.5 h-3.5" />Universe rooms</span><span>{arch?.opened}/{arch?.total}</span></div>
              <Progress value={(arch?.opened / Math.max(1, arch?.total)) * 100} className="h-1.5 bg-white/[0.06]" />
            </div>
          </div>
          <Button variant="outline" className="mt-4 glass w-full" onClick={() => navigate("/path")}>Continue Path Mode</Button>
        </div>
      </div>

      {quick && <DecodeMiniCard data={quick} decodeType="Status Word" />}
    </div>
  );
}
